import { useEffect, useRef } from 'react';
import { useStore } from '../store';
import { ZONES } from '../config/zones';
import { fetchBriefing, synthesize } from '../api';
import { soundEngine } from '../audio/soundEngine';

const VOICES = { intro: 'sage', progressive: 'nova', traditional: 'onyx' };

// Headless controller that drives the Harpy Eagle's spatial audio briefings + captions.
export default function Narrator() {
  const currentZone = useStore((s) => s.currentZone);
  const replayNonce = useStore((s) => s.replayNonce);
  const neutralNonce = useStore((s) => s.neutralNonce);
  const bankNonce = useStore((s) => s.bankNonce);
  const compareNonce = useStore((s) => s.compareNonce);
  const bridgeCueNonce = useStore((s) => s.bridgeCueNonce);
  const entered = useStore((s) => s.entered);
  const guideVolume = useStore((s) => s.guideVolume);
  const audioEnabled = useStore((s) => s.audioEnabled);

  const runId = useRef(0);
  const audioRef = useRef(null);
  const mounted = useRef(false);

  // Play a synthesized clip through the eagle-spatialized <audio> element.
  const playUrl = (url) =>
    new Promise((resolve) => {
      const a = audioRef.current;
      if (!a) return resolve();
      syncGuideAudio(); // enforce mute/volume before the clip starts
      let done = false;
      const finish = () => { if (done) return; done = true; clearTimeout(to); resolve(); };
      const to = setTimeout(finish, 30000);
      a.onended = finish;
      a.onerror = finish;
      a.src = url;
      a.play().catch(finish);
    });

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  // Native <audio> mute fallback — enforce the guide-volume / master-mute state
  // directly on the element so the briefing goes silent instantly, even if
  // Web-Audio spatialization hasn't attached (or can't, cross-origin).
  const syncGuideAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    const st = useStore.getState();
    const v = Math.max(0, Math.min(1, st.guideVolume));
    a.volume = v;
    a.muted = !st.audioEnabled || v < 0.01;
  };

  // Keep the element in sync the moment the guide is muted / volume changes.
  useEffect(() => { syncGuideAudio(); }, [guideVolume, audioEnabled]);

  // Single source of truth for "the guide must be silent": Master Mute OR Mute Guide.
  const guideMuted = () => {
    const st = useStore.getState();
    return !st.audioEnabled || st.guideVolume < 0.01;
  };

  // Route the narration element through the binaural engine so it truly emits
  // from the eagle's 3D position (awaiting start() avoids the first-run race that
  // used to leave the very first briefing playing as flat stereo).
  const ensureSpatial = async () => {
    await soundEngine.start();
    soundEngine.attachNarration(audioRef.current);
  };

  const playSegments = async (segments, cancelled) => {
    for (const seg of segments) {
      if (cancelled()) return;
      useStore.getState().setBankFocus(seg.bank ?? null);
      soundEngine.setNarrationBank(seg.bank ?? null); // per-ear binaural bias
      useStore.getState().setCaption(seg.text, seg.label);
      if (guideMuted()) continue; // guide muted → skip AI speech entirely (100% silent)
      let url = null;
      try { url = await synthesize(seg.text, seg.voice || 'sage'); } catch (e) { url = null; }
      if (cancelled()) return;
      if (url) await playUrl(url);
      else await wait(Math.min(9000, 2500 + seg.text.length * 45));
      if (cancelled()) return;
    }
  };

  // ---- Zone briefings (tour + proximity) ----
  useEffect(() => {
    if (!entered) return;
    // Skip the very first run (on mount) so we never auto-narrate unprompted.
    if (!mounted.current) { mounted.current = true; return; }

    const myRun = ++runId.current;
    const zone = ZONES[currentZone];
    if (!zone) return;

    const { setCaption, setBankFocus, setNarrating, nextZone, setBriefingCache } = useStore.getState();
    const cancelled = () => runId.current !== myRun;

    (async () => {
      try {
        setNarrating(true);
        setBankFocus(null);
        setCaption('The Harpy Eagle circles the ' + zone.title + ' clearing…', 'THE HARPY EAGLE');

        const b = await fetchBriefing(zone.id);
        if (cancelled()) return;
        setBriefingCache(zone.id, b);

        await ensureSpatial();

        await playSegments([
          { label: 'THE HARPY EAGLE', text: b.intro, bank: null, voice: VOICES.intro },
          { label: 'LIBERAL · LEFT BANK', text: b.progressive, bank: 'progressive', voice: VOICES.progressive },
          { label: 'CONSERVATIVE · RIGHT BANK', text: b.traditional, bank: 'traditional', voice: VOICES.traditional },
        ], cancelled);
        if (cancelled()) return;

        setNarrating(false);
        setBankFocus(null);

        // Auto-advance the guided tour.
        if (!cancelled() && useStore.getState().tourActive) {
          await wait(1600);
          if (!cancelled()) nextZone();
        }
      } catch (e) {
        if (!cancelled()) setNarrating(false);
      }
    })();

    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} } };
  }, [currentZone, replayNonce, entered]);

  // ---- Neutral Channel briefing (Boat mode) ----
  useEffect(() => {
    if (!entered || neutralNonce === 0) return;

    const myRun = ++runId.current;
    const { setCaption, setBankFocus, setNarrating } = useStore.getState();
    const cancelled = () => runId.current !== myRun;

    (async () => {
      try {
        setNarrating(true);
        setBankFocus(null);
        setCaption('The Harpy Eagle glides low over the Neutral Channel…', 'THE HARPY EAGLE');

        const b = await fetchBriefing('neutral-channel');
        if (cancelled()) return;

        await ensureSpatial();

        await playSegments([
          { label: 'THE HARPY EAGLE · NEUTRAL CHANNEL', text: b.intro, bank: null, voice: 'sage' },
          { label: 'THE UNALIGNED · MODERATES', text: b.progressive, bank: null, voice: 'nova' },
          { label: 'THE INDEPENDENTS · CONTRARIANS', text: b.traditional, bank: null, voice: 'onyx' },
        ], cancelled);
        if (cancelled()) return;

        setNarrating(false);
      } catch (e) {
        if (!cancelled()) setNarrating(false);
      }
    })();

    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} } };
  }, [neutralNonce, entered]);

  // ---- Single-bank briefing (click a bank chip) ----
  useEffect(() => {
    if (!entered || bankNonce === 0) return;

    const myRun = ++runId.current;
    const st = useStore.getState();
    const bank = st.bankRequest;
    const zone = ZONES[st.currentZone];
    if (!zone || !bank) return;

    const { setCaption, setBankFocus, setNarrating, setBriefingCache } = st;
    const cancelled = () => runId.current !== myRun;

    (async () => {
      try {
        setNarrating(true);
        setBankFocus(bank);
        setCaption(`The Harpy Eagle turns to the ${bank === 'progressive' ? 'Left' : 'Right'} Bank…`, 'THE HARPY EAGLE');

        const b = await fetchBriefing(zone.id);
        if (cancelled()) return;
        setBriefingCache(zone.id, b);

        await ensureSpatial();

        const seg = bank === 'progressive'
          ? { label: 'LIBERAL · LEFT BANK', text: b.progressive, bank: 'progressive', voice: VOICES.progressive }
          : { label: 'CONSERVATIVE · RIGHT BANK', text: b.traditional, bank: 'traditional', voice: VOICES.traditional };
        await playSegments([seg], cancelled);
        if (cancelled()) return;

        setNarrating(false);
        setBankFocus(null);
      } catch (e) {
        if (!cancelled()) setNarrating(false);
      }
    })();

    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} } };
  }, [bankNonce, entered]);

  // ---- A/B Compare: Liberal then Conservative, back-to-back ----
  useEffect(() => {
    if (!entered || compareNonce === 0) return;

    const myRun = ++runId.current;
    const st = useStore.getState();
    const zone = ZONES[st.currentZone];
    if (!zone) return;

    const { setCaption, setBankFocus, setNarrating, setBriefingCache } = st;
    const cancelled = () => runId.current !== myRun;

    (async () => {
      try {
        setNarrating(true);
        setBankFocus(null);
        setCaption(`The Harpy Eagle weighs both banks of ${zone.title}…`, 'THE HARPY EAGLE · A/B COMPARE');
        await wait(700);

        const b = await fetchBriefing(zone.id);
        if (cancelled()) return;
        setBriefingCache(zone.id, b);

        await ensureSpatial();

        await playSegments([
          { label: 'LIBERAL · LEFT BANK', text: b.progressive, bank: 'progressive', voice: VOICES.progressive },
          { label: 'CONSERVATIVE · RIGHT BANK', text: b.traditional, bank: 'traditional', voice: VOICES.traditional },
        ], cancelled);
        if (cancelled()) return;

        setNarrating(false);
        setBankFocus(null);
      } catch (e) {
        if (!cancelled()) setNarrating(false);
      }
    })();

    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} } };
  }, [compareNonce, entered]);

  // ---- Bridge cue: fired the first time the SUV is blocked by the river ----
  useEffect(() => {
    if (!entered || bridgeCueNonce === 0) return;

    const myRun = ++runId.current;
    const { setCaption, setBankFocus, setNarrating } = useStore.getState();
    const cancelled = () => runId.current !== myRun;

    (async () => {
      try {
        setNarrating(true);
        setBankFocus(null);
        const text = 'That is the river, not a road. To reach the other bank, take the Bridge of Consensus at the center of the map.';
        setCaption(text, 'THE HARPY EAGLE');
        await ensureSpatial();
        if (!guideMuted()) {
          let url = null;
          try { url = await synthesize(text, 'sage'); } catch (e) { url = null; }
          if (cancelled()) return;
          if (url) await playUrl(url); else await wait(4000);
        }
        if (cancelled()) return;
        setNarrating(false);
      } catch (e) {
        if (!cancelled()) setNarrating(false);
      }
    })();

    return () => { if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} } };
  }, [bridgeCueNonce, entered]);

  return <audio ref={audioRef} crossOrigin="anonymous" data-testid="narration-audio" style={{ display: 'none' }} />;
}
